import { type ReactNode, useMemo } from 'react';
import type { TypographyProps } from '../../../typography';
import { defaultValueStackedTypographyProps, defaultValueTypographyProps } from '../item/value';
import { ReadonlyList, type ReadonlyListProps } from '../readonly-list';
import { ChangeNotice } from './change-notice/change-notice';

export interface DiffedReadonlyListProps extends ReadonlyListProps {
  changeItems?: ReadonlyListProps['items'];
  changeLabel?: ReactNode;
}

function buildMatchedListItems(
  items: DiffedReadonlyListProps['items'],
  changeItems: DiffedReadonlyListProps['changeItems'] | undefined,
  changeLabel: ReactNode,
) {
  if (!changeItems) {
    return items;
  }

  const itemIds = Array.from(new Set([...items.map((item) => item.id), ...changeItems.map((item) => item.id)]));

  return itemIds.reduce<DiffedReadonlyListProps['items']>((acc, id) => {
    const item = items.find((x) => x.id === id);
    const changeItem = changeItems.find((x) => x.id === id);

    // Handle value change/removal
    if (item && changeItem) {
      acc.push({
        ...item,
        hideOverflow: true,
        valueTypography: {
          ...(item.columns === 1 ? defaultValueStackedTypographyProps : defaultValueTypographyProps),
          ...item.valueTypography,
          decoration: 'strikethrough',
        } as TypographyProps,
        notice: <ChangeNotice defaultRenderWhenEmpty="--" {...changeItem} label={changeLabel} />,
      });

      return acc;
    }

    // Handle value addition
    if (changeItem) {
      acc.push({
        ...changeItem,
        hideOverflow: true,
        value: { string: { value: undefined } },
        notice: <ChangeNotice defaultRenderWhenEmpty="--" {...changeItem} label={changeLabel} />,
      });

      return acc;
    }

    if (item) {
      acc.push(item);
    }

    return acc;
  }, []);
}

export function DiffedReadonlyList({ changeItems, changeLabel, items, ...rest }: DiffedReadonlyListProps) {
  const matchedItems = useMemo(() => buildMatchedListItems(items, changeItems, changeLabel), [items, changeItems, changeLabel]);

  return <ReadonlyList items={matchedItems} {...rest} />;
}
